import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert, CircularProgress, Link,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
} from '@mui/material';
import { formatCurrency, formatDate } from '../../utils/accounting/formatters';
import { serverMessage } from '../../utils/apiConvert';
import { FINE_TYPES, ADDON_TYPES } from '../../pages/work-orders/WorkOrderPaymentDialog';

/**
 * What was paid with one saved customer card: government fees, fines and add-ons on work orders and direct invoices.
 * The TC sees every payment with its status and the reason of a reversal; the customer only the payments that stand
 * (the backend already leaves the rest out, so `showStatus` only decides whether the status column is drawn).
 */

const PAGE_SIZE = 20;
const STATUS_LABEL = { RECORDED: 'Paid', REVERSED: 'Reversed', REFUNDED: 'Refunded', WRITTEN_OFF: 'Written off' };
const STATUS_COLOR = { RECORDED: 'success.main', REVERSED: 'text.disabled', REFUNDED: 'info.main', WRITTEN_OFF: 'warning.main' };

const itemLabel = (t) => {
  if (t.component === 'FINE') return t.itemType ? `Fine - ${FINE_TYPES[t.itemType] || t.itemType}` : 'Fine';
  if (t.component === 'ADDON') return t.itemType ? `Add-on - ${ADDON_TYPES[t.itemType] || t.itemType}` : 'Add-on';
  return 'Government fee';
};

const CardTransactionsDialog = ({ open, card, onClose, load, documentPath, showStatus }) => {
  const [page, setPage] = useState(0);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // The card's whole history comes in one call (newest first); the table pages through it here.
  useEffect(() => {
    if (!open || !card) return undefined;
    let cancelled = false;
    setPage(0);
    setLoading(true);
    setError(null);
    load(card.id)
      .then((items) => { if (!cancelled) setAll(items || []); })
      .catch((e) => { if (!cancelled) setError(serverMessage(e, 'Could not load the transactions')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, card, load]);

  const rows = all.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>
        Transactions{card ? ` - ${card.nickname} (•••• ${card.lastFour})` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {loading && rows.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', py: 2 }}>
            Nothing has been paid with this card yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Work order / invoice</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  {showStatus && <TableCell>Status</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((t) => {
                  const path = documentPath(t);
                  return (
                    <TableRow key={t.paymentId} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(t.paidAt)}</TableCell>
                      <TableCell>
                        {path ? <Link component={RouterLink} to={path} underline="hover" onClick={onClose}>{t.documentNumber}</Link> : t.documentNumber}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8125rem' }}>{t.serviceName}</Typography>
                        {t.customerName && <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>{t.customerName}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8125rem' }}>{itemLabel(t)}</Typography>
                        {t.paidToName && <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>{t.paidToName}</Typography>}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{formatCurrency(t.amount)}</TableCell>
                      {showStatus && (
                        <TableCell>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: STATUS_COLOR[t.status] }}>
                            {STATUS_LABEL[t.status] || t.status}
                          </Typography>
                          {t.closedReason && <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>{t.closedReason}</Typography>}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={all.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CardTransactionsDialog;
